// This file contains logic around checking whether or not the file path in
// a run's history points to a file which is already available in the backend.
// If it's not available, we iterate backwards through a run's history
// to find an existing file path from the closest previous step.

import {isNotNullOrUndefined} from '@wandb/weave/common/util/types';
import _ from 'lodash';

import {type RunHistoryRow} from '../../../types/run';
import {latestStepWithMedia, stepsWithKey} from '../../../util/media';
import {type TileMediaSpec} from '../../MediaCard';
import {isArtifactRef} from './artifacts';
import {type BaseFetchParams, fetchExistingFilePathSet} from './batching';
import {ensureHistoryRows} from './history';
import {updateTracking} from './inMemoryCache';
import {getKeyForTileMediaSpec} from './keys';
import {getMediaFilePath} from './paths';
import {
  getZeroIndexFilePath,
  shouldTryTensorboardSpecialHandling,
} from './tensorboard';
import {type ArtifactURISpec, type FilePathSpec, type PathSpec} from './types';

const AVAILABLE_FILES_BATCH_SIZE = 100;
const AVAILABLE_FILES_MAX_STEPS_CHECKED = 1000;

type GetLastAvailableFilePathForTileMediaSpecParams = {
  tileMediaSpec: TileMediaSpec;
  attemptNonexistentSteps: boolean;
  stepNumber: number;
};

// Loop backwards starting at the requested step to find an existing file path
export async function getLastAvailableFilePathForTileMediaSpec({
  tileMediaSpec,
  attemptNonexistentSteps,
  stepNumber,
}: GetLastAvailableFilePathForTileMediaSpecParams & {
  stepNumber?: number;
}): Promise<PathSpec | null> {
  const {run} = tileMediaSpec;
  const {latestStep, stepsWithMedia} = getStepMetadata(
    tileMediaSpec,
    attemptNonexistentSteps
  );
  if (latestStep == null) {
    return null;
  }

  /**
   * Filtering rows to only those with matching media keys to support retrieving
   * history data from multiple specs.
   *
   * When querying for multiple keys in a single query, history rows contain mulitple keys:
   * ```json
   * [
   *   {_step: 0, image_batch: {…}, single_image: {…}},
   *   {_step: 1, image_batch: {…}, single_image: {…}},
   * ]
   * ```
   *
   * When splitting the query into multiple specs, history rows contain only the keys for the spec:
   * ```json
   * [
   *   {_step: 0, image_batch: {…}},
   *   {_step: 1, image_batch: {…}},
   *   {_step: 0, single_image: {…}},
   *   {_step: 1, single_image: {…}},
   * ]
   * ```
   *
   * @see PanelMediaBrowser/index.ts#transformQuery
   */
  const mediaKey = tileMediaSpec.mediaKey;
  const historyRows = (run.history ?? []).filter(r => r[mediaKey] != null);
  const historyRowByStep: Map<number, RunHistoryRow> = new Map(
    historyRows.map(r => [r._step, r])
  );

  let stepLimit = latestStep;
  let totalStepsChecked = 0;

  const stepsChecked = new Set<number>();

  // Check the exact step first if provided
  if (stepNumber !== undefined) {
    if (historyRowByStep.has(stepNumber)) {
      const exactStepRow = historyRowByStep.get(stepNumber);
      const {existingFilePath} = await checkStepBatch({
        tileMediaSpec,
        historyRows: [exactStepRow].filter(isNotNullOrUndefined),
      });
      if (existingFilePath != null) {
        return existingFilePath;
      }
    }
  }

  // Check for the latest available file in batches until we find one
  while (
    stepLimit >= 0 &&
    totalStepsChecked < AVAILABLE_FILES_MAX_STEPS_CHECKED
  ) {
    const stepsToCheck = getStepsToCheck(
      stepsWithMedia,
      stepLimit,
      stepsChecked
    );
    if (stepsToCheck.length === 0) {
      break;
    }

    if (attemptNonexistentSteps) {
      await ensureMissingHistoryRows({
        stepsToCheck,
        historyRowByStep,
        tileMediaSpec,
      });
    }

    const {existingFilePath, shouldContinue} = await checkStepBatch({
      tileMediaSpec,
      historyRows: stepsToCheck
        .map(s => historyRowByStep.get(s))
        .filter(isNotNullOrUndefined),
    });
    if (existingFilePath != null) {
      return existingFilePath;
    }
    if (!shouldContinue) {
      break;
    }

    totalStepsChecked += stepsToCheck.length;
    stepLimit = stepsToCheck[0] - 1;
  }

  return null;
}

// This function separates logic around deciding which steps to check
// between normal and "sample-friendly" mode.
function getStepMetadata(
  {run, mediaKey, mediaStep}: TileMediaSpec,
  attemptNonexistentSteps: boolean
): {
  latestStep: number | null;
  stepsWithMedia: number[];
} {
  if (attemptNonexistentSteps) {
    // In "sample-friendly" mode, we assume the run has steps with media that may be missing from the current history.
    // In this case, we check every step in a range, not just steps that exist.
    const sampledSteps: number[] = run.history?.map(h => h._step) ?? [];
    const maxSampledStep = _.max(sampledSteps) ?? 0;
    return {
      stepsWithMedia: _.range(maxSampledStep + 1),
      latestStep: mediaStep,
    };
  }

  const stepsWithMedia = stepsWithKey(run, mediaKey);
  const latestStep = latestStepWithMedia(stepsWithMedia, mediaStep);
  return {
    stepsWithMedia,
    latestStep,
  };
}

// The array of steps returned is kept in ascending order
function getStepsToCheck(
  stepsWithMedia: number[],
  upperBound: number,
  stepsChecked: Set<number>
): number[] {
  const result: number[] = [];

  for (let i = stepsWithMedia.length - 1; i >= 0; i--) {
    const step = stepsWithMedia[i];
    if (step > upperBound || stepsChecked.has(step)) {
      continue;
    }
    result.unshift(step);
    // Track these as checked so we don't check them again
    stepsChecked.add(step);
    if (result.length >= AVAILABLE_FILES_BATCH_SIZE) {
      break;
    }
  }

  return result;
}

type EnsureMissingHistoryRowsParams = {
  stepsToCheck: number[];
  historyRowByStep: Map<number, RunHistoryRow>;
  tileMediaSpec: TileMediaSpec;
};

// In "sample-friendly" mode, we fetch the unsampled history
// for a batch of steps prior to checking it
async function ensureMissingHistoryRows({
  stepsToCheck,
  historyRowByStep,
  tileMediaSpec: {runSignature, mediaKey},
}: EnsureMissingHistoryRowsParams): Promise<void> {
  const missingSteps = stepsToCheck.filter(s => !historyRowByStep.has(s));
  const minMissingStep = _.min(missingSteps);
  const maxMissingStep = _.max(missingSteps);
  if (minMissingStep != null && maxMissingStep != null) {
    await ensureHistoryRows({
      historyRowByStep,
      runSignature,
      historyKeys: [mediaKey],
      minStep: minMissingStep,
      maxStep: maxMissingStep + 1,
    });
  }
}

type CheckStepBatchParams = {
  tileMediaSpec: TileMediaSpec;
  historyRows: RunHistoryRow[];
};

type CheckStepBatchResult = {
  existingFilePath: PathSpec | null;
  shouldContinue: boolean;
};

// For a batch of steps, check for the first existing file path (going in descending step order)
async function checkStepBatch({
  tileMediaSpec,
  historyRows,
}: CheckStepBatchParams): Promise<CheckStepBatchResult> {
  const {runSignature} = tileMediaSpec;

  // Some paths in the history are actually artifact references.
  // We handle those specially for now
  // (we only use them if 0 non-artifact paths are found for this batch).
  const {filePathSpecs, artifactURISpecs} = getPathSpecs({
    tileMediaSpec,
    historyRows,
  });

  const lastArtifactURISpec = _.last(artifactURISpecs);
  if (filePathSpecs.length === 0 && lastArtifactURISpec != null) {
    // TODO: actually check if this artifact reference maps to a file that is already available
    return {
      existingFilePath: lastArtifactURISpec,
      shouldContinue: false,
    };
  }

  const filePaths = filePathSpecs.map(s => s.filePath);

  // Find out which of the file paths actually exist
  const existingFilePathSet = await fetchExistingFilePathSetMaybeTensorboard({
    tileMediaSpec,
    runSignature,
    filePaths,
  });
  // Error / unexpected behavior. Abort.
  if (existingFilePathSet == null) {
    return {existingFilePath: null, shouldContinue: false};
  }

  // Take the last (closest to the specified step) file path available
  const existingFilePath = _.last(
    filePathSpecs.filter(s => existingFilePathSet?.has(s.filePath))
  );
  if (existingFilePath != null) {
    return {
      existingFilePath,
      shouldContinue: false,
    };
  }

  // This batch didn't have any available files. Continue to the next batch.
  return {existingFilePath: null, shouldContinue: true};
}

type GetPathSpecsParams = {
  tileMediaSpec: TileMediaSpec;
  historyRows: RunHistoryRow[];
};

// Splits the paths found in the batch of history rows into 2 distinct categories:
// File paths (pointing to files found in the Run Files tab) and artifact references.
function getPathSpecs({tileMediaSpec, historyRows}: GetPathSpecsParams): {
  filePathSpecs: FilePathSpec[];
  artifactURISpecs: ArtifactURISpec[];
} {
  const filePathSpecs: FilePathSpec[] = [];
  const artifactURISpecs: ArtifactURISpec[] = [];
  for (const historyRow of historyRows) {
    const filePath = getMediaFilePath({...tileMediaSpec, historyRow});
    if (!filePath) {
      continue;
    }
    if (isArtifactRef(filePath)) {
      artifactURISpecs.push({
        historyRow,
        artifactURI: filePath,
      });
    } else {
      filePathSpecs.push({
        historyRow,
        filePath,
      });
    }
  }
  return {filePathSpecs, artifactURISpecs};
}

type FetchExistingFilePathSetMaybeTensorboardParams = BaseFetchParams & {
  tileMediaSpec: TileMediaSpec;
};

// Special case one-off HAXXX to handle old data logged using Tensorboard.
// There was a W&B client bug that caused file paths to be constructed incorrectly.
async function fetchExistingFilePathSetMaybeTensorboard({
  tileMediaSpec,
  runSignature,
  filePaths,
}: FetchExistingFilePathSetMaybeTensorboardParams): ReturnType<
  typeof fetchExistingFilePathSet
> {
  const existingFilePathSet = await fetchExistingFilePathSet({
    runSignature,
    filePaths,
  }).then(result => {
    if (result != null) {
      const key = getKeyForTileMediaSpec(tileMediaSpec);
      const now = performance.now();
      updateTracking(key, 'availableFilesReceivedPerformanceMs', now);
      // URLs arrive in the same response — share the captured instant so
      // the urls stage duration reads exactly 0ms.
      updateTracking(key, 'directUrlReceivedPerformanceMs', now);
    }
    return result;
  });
  if (existingFilePathSet == null) {
    return null;
  }

  // For certain Tensorboard runs, if normal behavior doesn't return any results,
  // try again with special-cased file paths.
  if (
    existingFilePathSet.size === 0 &&
    (await shouldTryTensorboardSpecialHandling(tileMediaSpec))
  ) {
    const zeroIndexFilePaths = filePaths.map(getZeroIndexFilePath);
    return fetchExistingFilePathSet({
      runSignature,
      filePaths: zeroIndexFilePaths,
    });
  }

  return existingFilePathSet;
}
